import React from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { formatTimeAgo } from '../../../utils/date';
import { normalizeLanguageCode } from '../../../utils/platform';
import { useTranslation } from 'react-i18next';
import Badge from '../../common/Badge';
import HoverPlayButton from '../../common/HoverPlayButton';
import type { BaseMediaThumbnailProps } from '../../media/MediaThumbnail';

interface ShortsThumbnailProps extends Omit<BaseMediaThumbnailProps, 'aspectRatio' | 'navigationPath'> {
  views?: number;
  likes?: number;
}

export const ShortsThumbnail: React.FC<ShortsThumbnailProps> = (props) => {
  const { i18n } = useTranslation();
  
  const formatViews = (views: number) => {
    if (views >= 1000000) {
      return `${(views / 1000000).toFixed(1)}M`;
    }
    if (views >= 1000) {
      return `${(views / 1000).toFixed(1)}K`;
    }
    return views.toString();
  };

  const isNew = () => {
    const now = new Date();
    const created = new Date(props.createdAt);
    const diffHours = (now.getTime() - created.getTime()) / (1000 * 60 * 60);
    return diffHours <= 24;
  };
  
  return (
    <div className="group relative w-full">
      <Link 
        to={`/shorts/${props.id}`}
        className="block w-full focus:outline-none"
      >
        <div className="relative w-full aspect-[9/16] rounded-xl overflow-hidden">
          <img
            src={props.thumbnail}
            alt={props.title}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
            loading="lazy"
          />
          {isNew() && <Badge type="new" />}
          <HoverPlayButton icon="ph:play-thin" />
        </div>
        <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/95 via-black/80 to-transparent h-2/3 flex flex-col justify-end rounded-b-[0.7rem]">
          <h3 className="text-white text-sm font-medium line-clamp-2 break-words group-hover:text-primary-500 transition-colors duration-200">
            {props.title}
          </h3>
          <div className="flex items-center gap-2 mt-1 text-white/90 text-xs">
            {props.views !== undefined && (
              <>
                <span>{formatViews(props.views)} views</span>
                <span>•</span>
              </>
            )}
            <time dateTime={props.createdAt.toString()}>
              {formatTimeAgo(props.createdAt, normalizeLanguageCode(i18n.language))}
            </time>
          </div>
        </div>
        <div className="absolute top-3 right-3 z-10">
          <Icon 
            icon="mingcute:more-2-fill" 
            className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200"
          />
        </div>
      </Link>
    </div>
  );
};

export default ShortsThumbnail; 